

import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { buildApiUrl } from '../utils/api';
import { getWhatsAppUrl } from '../content/site-info';

export default function OrcamentoForm() {
  const [nome, setNome] = useState("");
  const [emailOrcamento, setEmailOrcamento] = useState("");
  const [servico, setServico] = useState("");
  const [errors, setErrors] = useState({});
  const [formStatus, setFormStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const validarFormulario = useCallback(() => {
    const erros = {};
    if (!nome.trim()) erros.nome = "Nome é obrigatório.";
    if (!emailOrcamento.trim()) erros.emailOrcamento = "E-mail é obrigatório.";
    if (!servico.trim()) erros.servico = "Serviço é obrigatório.";
    return erros;
  }, [nome, emailOrcamento, servico]);

  useEffect(() => {
    setErrors(validarFormulario());
  }, [validarFormulario]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const erros = validarFormulario();
    setErrors(erros);
    if (Object.keys(erros).length > 0) return;

    setLoading(true);

    try {
      // 1. Constrói a mensagem e o assunto para satisfazer o esquema de Contact
      const requestSubject = `Solicitação de Orçamento via Formulário: ${servico}`;
      const requestMessage = `Olá, gostaria de agendar um orçamento para o serviço de ${servico}. Meu nome é ${nome} e meu e-mail para contato é ${emailOrcamento}. Por favor, entre em contato o mais breve possível.`;

      // CORREÇÃO CRÍTICA: Mapeamento dos campos do formulário para o schema de Contact
      const contactBody = {
        name: nome,
        email: emailOrcamento,
        subject: requestSubject, // Campo 'subject' agora está presente
        message: requestMessage, // Campo 'message' agora está presente
      };

      await fetch(buildApiUrl('/api/contact'), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Envia o novo corpo com os campos 'subject' e 'message'
        body: JSON.stringify(contactBody),
      });

      setFormStatus("success");
      
      // 2. Lógica do WhatsApp permanece inalterada
      const msg = `Olá! Meu nome é ${nome}, e-mail ${emailOrcamento}, e desejo agendar um orçamento para ${servico}.`;
      window.open(getWhatsAppUrl(msg), '_blank');

      setNome("");
      setEmailOrcamento("");
      setServico("");
    } catch (error) {
      console.error("Erro na requisição de contato:", error);
      setFormStatus("error");
    } finally {
      setLoading(false);
      setTimeout(() => setFormStatus(null), 4000);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
      className="w-full min-w-0"
    >
      <motion.h2
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="text-xl xs:text-2xl xs2:text-2xl xs3:text-3xl sm:text-3xl 
                   md:text-4xl font-bold mb-4 xs:mb-5 sm:mb-6 text-center 
                   text-primary-700"
      >
        Agende seu Orçamento
      </motion.h2>
      <form onSubmit={handleSubmit} className="w-full max-w-xl mx-auto 
                                              space-y-4 xs:space-y-5 sm:space-y-6">
        <div>
          <label htmlFor="nome" className="block text-xs xs:text-sm sm:text-base 
                                         font-semibold text-gray-700 mb-1 xs:mb-2">
            Seu nome <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="nome"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            className={`input-modern ${
              errors.nome 
                ? "border-red-500 focus:border-red-500 focus:ring-red-200" 
                : ""
            }`}
            placeholder="Digite seu nome completo"
            aria-invalid={!!errors.nome}
            aria-describedby={errors.nome ? 'nome-error' : undefined}
          />
          {errors.nome && (
            <p 
              id="nome-error" 
              className="text-red-600 text-xs xs:text-sm mt-1 flex items-center gap-1"
              role="alert"
            >
              <span>⚠️</span> {errors.nome}
            </p>
          )}
        </div>
        <div>
          <label htmlFor="emailOrcamento" className="block text-xs xs:text-sm sm:text-base 
                                                   font-semibold text-gray-700 mb-1 xs:mb-2">
            Seu e-mail <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="emailOrcamento"
            value={emailOrcamento}
            onChange={(e) => setEmailOrcamento(e.target.value)}
            className={`input-modern ${
              errors.emailOrcamento 
                ? "border-red-500 focus:border-red-500 focus:ring-red-200" 
                : ""
            }`}
            placeholder="seu@email.com"
            aria-invalid={!!errors.emailOrcamento}
            aria-describedby={errors.emailOrcamento ? 'email-error' : undefined}
          />
          {errors.emailOrcamento && (
            <p 
              id="email-error" 
              className="text-red-600 text-xs xs:text-sm mt-1 flex items-center gap-1"
              role="alert"
            >
              <span>⚠️</span> {errors.emailOrcamento}
            </p>
          )}
        </div>
        <div>
          <label htmlFor="servico" className="block text-xs xs:text-sm sm:text-base 
                                            font-semibold text-gray-700 mb-1 xs:mb-2">
            Serviço desejado <span className="text-red-500">*</span>
          </label>
          <select
            id="servico"
            value={servico}
            onChange={(e) => setServico(e.target.value)}
            className={`input-modern ${
              errors.servico 
                ? "border-red-500 focus:border-red-500 focus:ring-red-200" 
                : ""
            }`}
            aria-invalid={!!errors.servico}
            aria-describedby={errors.servico ? 'servico-error' : undefined}
          >
            <option value="">Selecione um serviço</option>
            <option value="Notebook">Notebook</option>
            <option value="PC">PC</option>
            <option value="TV">TV</option>
            <option value="Microondas">Microondas</option>
            <option value="No-break">No-break</option>
          </select>
          {errors.servico && (
            <p 
              id="servico-error" 
              className="text-red-600 text-xs xs:text-sm mt-1 flex items-center gap-1"
              role="alert"
            >
              <span>⚠️</span> {errors.servico}
            </p>
          )}
        </div>
        <button
          type="submit"
          disabled={loading}
          className="btn-primary w-full text-sm xs:text-base sm:text-lg"
        >
          {loading ? (
            <>
              <svg className="animate-spin h-4 w-4 xs:h-5 xs:w-5 inline-block mr-2" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Enviando...
            </>
          ) : (
            <>
              <span>✨</span> Agendar via WhatsApp
            </>
          )}
        </button>
        <AnimatePresence>
          {formStatus === "success" && (
            <motion.p 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }} 
              className="text-green-600 text-xs xs:text-sm sm:text-base text-center 
                       font-medium mt-2 flex items-center justify-center gap-2"
            >
              <span>✅</span> Dados enviados. Redirecionando para o WhatsApp...
            </motion.p>
          )}
          {formStatus === "error" && (
            <motion.p 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }} 
              className="text-red-600 text-xs xs:text-sm sm:text-base text-center 
                       font-medium mt-2 flex items-center justify-center gap-2"
            >
              <span>❌</span> Ocorreu um erro ao salvar seu contato. Tente novamente mais tarde.
            </motion.p>
          )}
        </AnimatePresence>
      </form>
    </motion.div>
  );
} 

